import { useEffect, useRef, useCallback } from 'react'
import { getAudioKey, hasAudioRecording } from './useWordAudio'
import { getSignedUrl } from '../utils/S3Upload'

const FEMALE_VOICE_USER_ID = '6605279e1d5babe34ff11035'
const MALE_VOICE_USER_ID = '675a2b3c4d5e6f7890123456'
const MAX_PRELOAD_COUNT = 50 // Limit to prevent too many requests

interface WordData {
  _id: string
  has_audio?: boolean
}

export const useAudioPreloader = (storyDictionary: { [word: string]: WordData } | null) => {
  const preloadedKeys = useRef<Set<string>>(new Set())

  const preloadAudio = useCallback(async (key: string) => {
    if (preloadedKeys.current.has(key)) return

    try {
      const signedUrl = await getSignedUrl({ key })
      const link = document.createElement('link')
      link.rel = 'prefetch'
      link.href = signedUrl
      link.as = 'audio'
      document.head.appendChild(link)
      preloadedKeys.current.add(key)
    } catch {
      // Silent fail - preloading is optional
    }
  }, [])

  useEffect(() => {
    if (!storyDictionary) return

    const words = Object.values(storyDictionary)
      .filter((word) => hasAudioRecording(word.has_audio))
      .slice(0, MAX_PRELOAD_COUNT)

    words.forEach((word) => {
      preloadAudio(getAudioKey(word._id, MALE_VOICE_USER_ID))
      preloadAudio(getAudioKey(word._id, FEMALE_VOICE_USER_ID))
    })

    // Cleanup function
    return () => {
      preloadedKeys.current.clear()
    }
  }, [storyDictionary, preloadAudio])
}
